import type { QueryClient } from '@tanstack/react-query';
import { MASTER_DATA_QUERY_KEY } from './hooks';
import { SLOT_VALUATION_INPUTS_QUERY_KEY } from './slot-tearsheet/hooks';
import type { SlotOverrideOperation } from './types';

export interface InvalidateOverrideSurfacesOptions {
  /** The operation names a composite wellbore's leg (no row is that slot): refetch the grids. */
  targetsCompositeLeg?: boolean;
}

/**
 * The three caches one slot-override operation makes stale, marked together.
 *
 * ── WHY THIS IS ONE FUNCTION AND NOT TWO COPIES ──────────────────────────────────────────
 *
 * An override is not a fact about the slots grid; it is a fact about the SLOT, and three
 * surfaces render it:
 *
 *   [MASTER_DATA, packageId, 'slots']        the properties grid
 *   [MASTER_DATA, packageId, 'allocations']  the allocations view, the same slots by tract
 *   [SLOT_VALUATION_INPUTS, packageId]       every cached TEARSHEET on the package
 *
 * `commitSlotOverride` (a grid or tearsheet edit) marked all three. `commitAgentOverride`
 * — the same operations, staged by the assistant instead of typed — marked none of them:
 * it painted the loaded rows and appended to the working config, and stopped there. So every
 * open tearsheet went on showing pre-override values after an agent edit, and the BE-derived
 * cascades the paint cannot compute (the AFE cost columns) never reconciled at all, because
 * nothing was ever stale to reconcile.
 *
 * ⚠️ THE ALLOCATIONS LINE IS NOT WHAT THIS FIXES, AND AN EARLIER VERSION OF THIS COMMENT
 * CLAIMED IT WAS. That read does not carry the pending log —
 * `slot-allocation-view.tsx` calls `useMasterDataView` WITHOUT `slotOverrides`, where the
 * slots grid passes it — so a pending edit can never appear there whatever is invalidated,
 * and refetching it returns the committed values again. Invalidating the key is still right
 * (it reconciles once a run commits), but it does not make allocations reflect pending work,
 * and this comment must not say otherwise. Fixing that is a separate change.
 *
 * Two copies of "which surfaces does an override touch" is how they came to disagree in
 * the first place, so there is one copy and both callers hold it.
 *
 * ── THE REFETCH TYPES, AND WHY THE SLOTS AND ALLOCATIONS KEYS TURN ON `kind` ─────────────
 *
 * `refetchType: 'none'` means "mark stale, refetch on next use", which is enough for a SET
 * because `paintNewestOperation` has already put the value on screen. An UNSET cannot be
 * painted — what it reveals is the remaining log folded by SlotOverrideResolver, which only
 * the server can compute — so on a MOUNTED view the reverted-away value simply stays there:
 * "next use" never comes while the user is looking at it. Hence `'active'` on a revert, for
 * the two grid views.
 *
 * 'active' fires only for a query with a mounted observer and these reads are
 * server-paginated, so a revert costs one request for the visible page, per revert CLICK —
 * not one per surface per keystroke, which is what `'none'` on a set is protecting against.
 *
 * The TEARSHEET key stays 'none' in both cases: it is invalidated at the `packageId` prefix
 * rather than the exact slot (a bulk or filter operation touches slots whose tearsheets are
 * cached but whose ids the caller cannot enumerate), so 'active' there would refetch every
 * cached tearsheet on the package.
 *
 * @param {QueryClient} queryClient - The page's react-query client.
 * @param {number} packageId - The package whose caches are keyed.
 * @param {SlotOverrideOperation} operation - The operation just committed.
 * @param {InvalidateOverrideSurfacesOptions} options - Whether the operation names a composite
 *   wellbore's leg, which refetches the grids as a revert does.
 * @return {void}
 */
export function invalidateOverrideSurfaces(
  queryClient: QueryClient,
  packageId: number,
  operation: SlotOverrideOperation,
  options: InvalidateOverrideSurfacesOptions = {}
): void {
  // A Volume Source override on a composite wellbore's LEG is painted onto the leg, but the
  // row's own source — the recipe rebuilt over the legs with the new curve's lateral — is the
  // server's to fold, so it refetches as a revert does.
  const refetchType =
    operation.kind === 'unset' || options.targetsCompositeLeg
      ? 'active'
      : 'none';

  void queryClient.invalidateQueries({
    queryKey: [MASTER_DATA_QUERY_KEY, packageId, 'slots'],
    refetchType,
  });
  void queryClient.invalidateQueries({
    queryKey: [MASTER_DATA_QUERY_KEY, packageId, 'allocations'],
    refetchType,
  });
  void queryClient.invalidateQueries({
    queryKey: [SLOT_VALUATION_INPUTS_QUERY_KEY, packageId],
    refetchType: 'none',
  });
}
